import React, { useCallback, useEffect } from "react";
import { ReleasePageWrapper } from "../ReleasePageWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WizardService } from "../../../services/WizardService";
import {
  Button,
  Col,
  Row,
  Space,
  TablePaginationConfig,
  message,
  Drawer,
  Popconfirm,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Sdk } from "../../../types/sdk";
import { SdkForm } from "./SdkForm";
import { TableQueryFilter } from "../../components/TableQueryFilter";
import { SdkList, getSdkColumns } from "./SdkList";
import { TableColumnSelector } from "../../components/TableColumnSelector";

export const SdkListPage = () => {
  const api = WizardService.Sdk;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<Sdk[]>([]);
  const [selectedRecords, setSelectedRecords] = React.useState<number[]>([]);
  const [editing, setEditing] = React.useState<number | undefined>(undefined);
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>([]);

  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: searchParams.get("page") !== null ? +searchParams.get("page")! : 1,
    pageSize: searchParams.get("per") !== null ? +searchParams.get("per")! : 10,
    showSizeChanger: true,
  });

  const viewRecord = (id: number) => {
    navigate(id.toString());
  };

  const editRecord = (id: number) => {
    setEditing(id);
  };

  const copyRecord = (id: number) => {
    setIsLoading(true);
    api
      .copy(id)
      .then((copiedSdk) => {
        // Refresh the list first, then open the form
        return api.all(searchParams).then((response) => {
          setRecords(response.items || []);
          setPagination((state) => ({
            ...state,
            current: response.metadata.page,
            pageSize: response.metadata.per,
            total: response.metadata.total,
          }));
          // Now open the form with the copied record
          setEditing(copiedSdk.id);
        });
      })
      .catch((error) => {
        message.error(error.message);
        // Fallback to client-side copy if server doesn't support deep copy
        const originalRecord = records.find((r) => r.id === id);
        if (originalRecord) {
          // Create a copy with ID set to undefined to trigger creation flow
          const copiedRecord: Sdk = {
            ...originalRecord,
            id: undefined,
            // Modify the name to indicate it's a copy
            name: `${originalRecord.name} (Copy)`,
          };
          // Temporarily add the copied record to the records list so the form can access it
          setRecords([...records, copiedRecord]);
          setEditing(undefined);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const saveRecord = (record: Sdk) => {
    setIsLoading(true);
    let promise: Promise<any>;
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .then(() => {
        setEditing(undefined);
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeRecord = (id: number) => {
    setIsLoading(true);
    api
      .delete(id)
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeSelectedRecords = () => {
    setIsLoading(true);
    Promise.all(selectedRecords.map((id) => api.delete(id)))
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  // Callbacks
  const fetch = useCallback(() => {
    setSelectedRecords([]);
    setIsLoading(true);
    api
      .all(searchParams)
      .then((response) => {
        setRecords(response.items || []);
        setPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  }, [searchParams, api]);


  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <ReleasePageWrapper>
      <Row
        style={{
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Col flex="auto">
          <TableQueryFilter filter={["platform", "category", "pattern", "version", "stability"]} />
        </Col>
        <Col flex="right">
          <Space>
            <TableColumnSelector
              columns={getSdkColumns(true)}
              onChange={setVisibleColumns}
              storageKey="sdk-table-columns"
            />
            <Button
              type="primary"
              icon={<PlusOutlined  />}
              onClick={(_) => setEditing(0)}
            >
              Create
            </Button>
            <Popconfirm
              title="Delete Sdks"
              description="Are you sure to delete selected sdks?"
              onConfirm={removeSelectedRecords}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined  />}
                disabled={selectedRecords.length === 0}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col flex="100%">
          <SdkList
            loading={isLoading}
            records={records}
            pagination={pagination}
            onChange={(pagination) => {
              setPagination(pagination);
              const params = new URLSearchParams(searchParams);

              pagination.current
                ? params.set("page", pagination.current.toString())
                : params.delete("page");
              pagination.pageSize
                ? params.set("per", pagination.pageSize.toString())
                : params.delete("per");

              setSearchParams(params);
            }}
            viewRecord={viewRecord}
            editRecord={editRecord}
            copyRecord={copyRecord}
            removeRecord={removeRecord}
            selectRecods={setSelectedRecords}
            visibleColumns={visibleColumns}
            editable
          />
        </Col>
      </Row>
      <Drawer
        title={
          editing !== undefined && editing > 0
            ? "Edit Sdk Release #" + editing
            : "Create a new Sdk Release"
        }
        width={600}
        onClose={(_) => setEditing(undefined)}
        open={editing !== undefined}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <SdkForm
          sdk={records.filter((r) => r.id === editing)[0]}
          isLoading={isLoading}
          onFinish={saveRecord}
        />
      </Drawer>
    </ReleasePageWrapper>
  );
};
